/**
 * Accessibility Utilities
 * Provides ARIA labels, keyboard navigation, and screen reader support
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Generate unique IDs for accessibility
 */
export const generateA11yId = (prefix = 'a11y') => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * ARIA label generators for different component types
 */
export const getAriaLabels = {
  button: (action, context) => ({
    'aria-label': `${action}${context ? ` ${context}` : ''}`,
    role: 'button'
  }),
  
  link: (destination, context) => ({
    'aria-label': `Aller à ${destination}${context ? ` ${context}` : ''}`,
    role: 'link'
  }),
  
  input: (label, required = false, invalid = false) => ({
    'aria-label': label,
    'aria-required': required,
    'aria-invalid': invalid,
    role: 'textbox'
  }),
  
  select: (label, required = false) => ({
    'aria-label': label,
    'aria-required': required,
    role: 'combobox',
    'aria-haspopup': 'listbox'
  }),
  
  checkbox: (label, checked = false) => ({
    'aria-label': label,
    'aria-checked': checked,
    role: 'checkbox'
  }),
  
  radio: (label, checked = false, groupName) => ({
    'aria-label': label,
    'aria-checked': checked,
    role: 'radio',
    name: groupName
  }),
  
  modal: (title) => ({
    'aria-label': title,
    'aria-modal': 'true',
    role: 'dialog'
  }),
  
  alert: (message, type = 'info') => ({
    'aria-label': message,
    role: type === 'error' ? 'alert' : 'status',
    'aria-live': type === 'error' ? 'assertive' : 'polite'
  }),
  
  tab: (label, selected = false, controls) => ({
    'aria-label': label,
    'aria-selected': selected,
    role: 'tab',
    'aria-controls': controls
  }),
  
  tabpanel: (label, labelledby) => ({
    'aria-label': label,
    'aria-labelledby': labelledby,
    role: 'tabpanel'
  }),
  
  list: (label, itemCount) => ({
    'aria-label': `${label}${itemCount ? `, ${itemCount} éléments` : ''}`,
    role: 'list'
  }),
  
  listitem: (label, position, total) => ({
    'aria-label': `${label}${position && total ? `, élément ${position} sur ${total}` : ''}`,
    role: 'listitem'
  }),
  
  progressbar: (label, value, max = 100) => ({
    'aria-label': label,
    'aria-valuenow': value,
    'aria-valuemin': 0,
    'aria-valuemax': max,
    'aria-valuetext': `${value}%`,
    role: 'progressbar'
  }),
  
  grid: (label, rows, cols) => ({
    'aria-label': `${label}, ${rows} lignes, ${cols} colonnes`,
    role: 'grid'
  }),
  
  gridcell: (label, row, col) => ({
    'aria-label': label,
    role: 'gridcell',
    'aria-rowindex': row,
    'aria-colindex': col
  })
};

/**
 * Keyboard navigation hook
 */
export const useKeyboardNavigation = (items, options = {}) => {
  const {
    orientation = 'vertical', // 'vertical', 'horizontal', 'both'
    loop = true,
    onSelect,
    onEscape,
    disabled = false
  } = options;

  const [focusedIndex, setFocusedIndex] = useState(0);
  const itemRefs = useRef([]);

  const handleKeyDown = useCallback((event) => {
    if (disabled || !items.length) return;

    const { key } = event;
    let newIndex = focusedIndex;

    switch (key) {
      case 'ArrowDown':
        if (orientation === 'vertical' || orientation === 'both') {
          event.preventDefault();
          newIndex = focusedIndex + 1;
          if (newIndex >= items.length) {
            newIndex = loop ? 0 : items.length - 1;
          }
        }
        break;
        
      case 'ArrowUp':
        if (orientation === 'vertical' || orientation === 'both') {
          event.preventDefault();
          newIndex = focusedIndex - 1;
          if (newIndex < 0) {
            newIndex = loop ? items.length - 1 : 0;
          }
        }
        break;
        
      case 'ArrowRight':
        if (orientation === 'horizontal' || orientation === 'both') {
          event.preventDefault();
          newIndex = focusedIndex + 1;
          if (newIndex >= items.length) {
            newIndex = loop ? 0 : items.length - 1;
          }
        }
        break;
        
      case 'ArrowLeft':
        if (orientation === 'horizontal' || orientation === 'both') {
          event.preventDefault();
          newIndex = focusedIndex - 1;
          if (newIndex < 0) {
            newIndex = loop ? items.length - 1 : 0;
          }
        }
        break;
        
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
        
      case 'End':
        event.preventDefault();
        newIndex = items.length - 1;
        break;
        
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (onSelect) {
          onSelect(items[focusedIndex], focusedIndex);
        }
        break;
        
      case 'Escape':
        event.preventDefault();
        if (onEscape) {
          onEscape();
        }
        break;
        
      default:
        return;
    }

    if (newIndex !== focusedIndex) {
      setFocusedIndex(newIndex);
      itemRefs.current[newIndex]?.focus();
    }
  }, [focusedIndex, items, orientation, loop, onSelect, onEscape, disabled]);

  const setItemRef = useCallback((index, element) => {
    itemRefs.current[index] = element;
  }, []);

  return {
    focusedIndex,
    setFocusedIndex,
    handleKeyDown,
    setItemRef
  };
};

/**
 * Focus management hook
 */
export const useFocusManagement = () => {
  const previousFocus = useRef(null);

  const saveFocus = useCallback(() => {
    previousFocus.current = document.activeElement;
  }, []);

  const restoreFocus = useCallback(() => {
    if (previousFocus.current && typeof previousFocus.current.focus === 'function') {
      previousFocus.current.focus();
    }
  }, []);

  const trapFocus = useCallback((containerRef) => {
    if (!containerRef.current) return;

    const focusableElements = containerRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    containerRef.current.addEventListener('keydown', handleTabKey);
    firstElement?.focus();

    return () => {
      containerRef.current?.removeEventListener('keydown', handleTabKey);
    };
  }, []);

  return {
    saveFocus,
    restoreFocus,
    trapFocus
  };
};

/**
 * Screen reader announcements
 */
export const useScreenReader = () => {
  const announcementRef = useRef(null);

  useEffect(() => {
    // Create announcement element if it doesn't exist
    if (!announcementRef.current) {
      const element = document.createElement('div');
      element.setAttribute('aria-live', 'polite');
      element.setAttribute('aria-atomic', 'true');
      element.style.position = 'absolute';
      element.style.left = '-10000px';
      element.style.width = '1px';
      element.style.height = '1px';
      element.style.overflow = 'hidden';
      document.body.appendChild(element);
      announcementRef.current = element;
    }

    return () => {
      // Safely remove the element
      if (announcementRef.current && announcementRef.current.parentNode) {
        try {
          announcementRef.current.parentNode.removeChild(announcementRef.current);
        } catch (error) {
          console.warn('Screen reader element already removed:', error);
        }
        announcementRef.current = null;
      }
    };
  }, []);

  const announce = useCallback((message, priority = 'polite') => {
    if (announcementRef.current && announcementRef.current.parentNode) {
      try {
        announcementRef.current.setAttribute('aria-live', priority);
        announcementRef.current.textContent = message;

        // Clear after announcement
        setTimeout(() => {
          if (announcementRef.current && announcementRef.current.parentNode) {
            announcementRef.current.textContent = '';
          }
        }, 1000);
      } catch (error) {
        console.warn('Error making screen reader announcement:', error);
      }
    }
  }, []);

  return { announce };
};

/**
 * Skip link component for keyboard navigation
 */
export const SkipLink = ({ href, children, className = '' }) => (
  <a
    href={href}
    className={`
      absolute -top-40 left-6 z-50 bg-blue-600 text-white px-4 py-2 rounded
      focus:top-6 transition-all duration-200
      ${className}
    `}
    {...getAriaLabels.link(children, 'lien de navigation rapide')}
  >
    {children}
  </a>
);

/**
 * Accessible button component
 */
export const AccessibleButton = ({
  children,
  onClick,
  disabled = false,
  variant = 'primary',
  size = 'md',
  ariaLabel,
  ariaDescribedBy,
  className = '',
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';
  
  const variantClasses = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 disabled:bg-gray-300',
    secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500 disabled:bg-gray-100',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 disabled:bg-gray-300'
  };
  
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      {...getAriaLabels.button(ariaLabel || children, '')}
      aria-describedby={ariaDescribedBy}
      {...props}
    >
      {children}
    </button>
  );
};

/**
 * Accessible form field component
 */
export const AccessibleFormField = ({
  label,
  children,
  error,
  required = false,
  helpText,
  className = ''
}) => {
  const fieldId = generateA11yId('field');
  const errorId = error ? generateA11yId('error') : undefined;
  const helpId = helpText ? generateA11yId('help') : undefined;

  return (
    <div className={`space-y-1 ${className}`}>
      <label
        htmlFor={fieldId}
        className="block text-sm font-medium text-gray-700"
      >
        {label}
        {required && <span className="text-red-500 ml-1" aria-label="requis">*</span>}
      </label>
      
      {React.cloneElement(children, {
        id: fieldId,
        'aria-describedby': [errorId, helpId].filter(Boolean).join(' '),
        'aria-invalid': !!error,
        'aria-required': required
      })}
      
      {helpText && (
        <p id={helpId} className="text-sm text-gray-500">
          {helpText}
        </p>
      )}
      
      {error && (
        <p id={errorId} className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default {
  generateA11yId,
  getAriaLabels,
  useKeyboardNavigation,
  useFocusManagement,
  useScreenReader,
  SkipLink,
  AccessibleButton,
  AccessibleFormField
};
